import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtIDR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Boxes, Plus, Trash2, Tag, Pencil, ImageIcon } from "lucide-react";
import { ProductPhotoSlot, productPhotoUrl } from "@/components/ProductPhotoSlot";

const EMPTY_DISC = { discount_type: "", discount_value: 0, discount_start: "", discount_end: "" };
const BLANK = { name: "", category: "", price: 0, stock: 0, low_stock_threshold: 5, image_id: "" };

export function CatalogPanel({ branch }) {
  const [products, setProducts] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [disc, setDisc] = useState(null);
  const [np, setNp] = useState(BLANK);
  const [catMode, setCatMode] = useState("select");
  const [filterCat, setFilterCat] = useState("Semua");
  const categories = useMemo(() => Array.from(new Set(products.map((p) => p.category).filter(Boolean))), [products]);
  const shown = filterCat === "Semua" ? products : products.filter((p) => p.category === filterCat);

  const load = async () => {
    if (!branch) return;
    try { const pr = await api.get("/products"); setProducts(pr.data.filter((p) => p.branch_id === branch.id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [branch?.id]);

  const openAdd = () => { setEditId(null); setNp({ ...BLANK, category: categories[0] || "" }); setCatMode(categories.length ? "select" : "new"); setShow(true); };
  const openEdit = (p) => { setEditId(p.id); setNp({ name: p.name, category: p.category, price: p.price, stock: p.stock, low_stock_threshold: p.low_stock_threshold, image_id: p.image_id || "" }); setCatMode("select"); setShow(true); };
  const saveProduct = async () => {
    if (!np.name.trim()) { toast.error("Nama produk wajib diisi"); return; }
    try {
      if (editId) { await api.put(`/products/${editId}`, { name: np.name, category: np.category, price: Number(np.price), stock: Number(np.stock), low_stock_threshold: Number(np.low_stock_threshold), image_id: np.image_id }); toast.success("Produk diperbarui"); }
      else { await api.post("/products", { ...np, price: Number(np.price), stock: Number(np.stock), low_stock_threshold: Number(np.low_stock_threshold), branch_id: branch.id }); toast.success("Produk ditambahkan"); }
      setShow(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delProduct = async (id) => { try { await api.delete(`/products/${id}`); toast.success("Produk dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveDisc = async () => {
    try { await api.put(`/products/${disc.id}`, { discount_type: disc.discount_type, discount_value: Number(disc.discount_value), discount_start: disc.discount_start, discount_end: disc.discount_end }); toast.success("Diskon disimpan"); setDisc(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!branch) return <Card className="card-surface rounded-2xl p-5 text-sm text-muted-foreground">Pilih cabang terlebih dahulu.</Card>;

  return (
    <>
      <Card className="card-surface rounded-2xl p-5" data-testid="catalog-panel">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><Boxes className="text-crimson" size={18} /> Katalog Menu</div>
          <div className="flex items-center gap-2">
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger data-testid="catalog-cat-filter" className="h-9 w-48 bg-neutral-100 border-neutral-200"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Semua">Semua Kategori</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="add-product-btn" onClick={openAdd} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Produk</Button>
          </div>
        </div>
        <div className="space-y-2">
          {shown.map((p) => {
            const low = p.stock <= p.low_stock_threshold;
            return (
              <div key={p.id} data-testid={`product-row-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-neutral-100 px-4 py-3">
                <div className="min-w-[160px] flex-1">
                  <div className="font-semibold text-neutral-900">{p.name}
                    {low && <span className="ml-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-500">LOW</span>}
                    {p.on_discount && <span className="ml-1 rounded-full bg-crimson/25 px-2 py-0.5 text-[10px] font-bold text-crimson">DISKON</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{p.category} · Stok {p.stock}
                    {p.on_discount
                      ? <span className="ml-2 text-gold">{fmtIDR(p.effective_price)} <span className="line-through opacity-60">{fmtIDR(p.price)}</span></span>
                      : <span className="ml-2 text-gold">{fmtIDR(p.price)}</span>}
                  </div>
                </div>
                {p.image_id
                  ? <img src={productPhotoUrl(p.image_id)} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
                  : <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-neutral-200 text-neutral-400"><ImageIcon size={16} /></div>}
                <Button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} variant="ghost" size="icon" className="text-neutral-500 hover:bg-crimson/10 hover:text-crimson"><Pencil size={16} /></Button>
                <Button data-testid={`disc-product-${p.id}`} onClick={() => setDisc({ ...EMPTY_DISC, ...p, discount_type: p.discount_type || "", discount_value: p.discount_value || 0, discount_start: p.discount_start || "", discount_end: p.discount_end || "" })} variant="ghost" size="icon" className="text-gold hover:bg-gold/10"><Tag size={16} /></Button>
                <Button data-testid={`del-product-${p.id}`} onClick={() => delProduct(p.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
              </div>
            );
          })}
          {shown.length === 0 && <p className="text-sm text-muted-foreground">Belum ada produk pada kategori ini.</p>}
        </div>
      </Card>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <ProductPhotoSlot value={np.image_id} onChange={(id) => setNp({ ...np, image_id: id })} />
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Nama Produk</Label>
                <Input data-testid="np-name" placeholder="mis. Udang Segar 250gr" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" />
                <p className="mt-1 text-[10px] text-muted-foreground">Foto opsional, tampil kotak kecil di kasir.</p>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Kategori</Label>
                <button type="button" data-testid="np-cat-toggle" onClick={() => setCatMode(catMode === "select" ? "new" : "select")} className="text-[11px] font-semibold text-gold hover:underline">{catMode === "select" ? "+ Kategori Baru" : "Pilih dari daftar"}</button>
              </div>
              {catMode === "select" && categories.length > 0 ? (
                <Select value={np.category} onValueChange={(v) => setNp({ ...np, category: v })}>
                  <SelectTrigger data-testid="np-category-select" className="mt-1 bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid="np-category" placeholder="Ketik kategori baru, mis. Dessert" value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" />
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Harga (Rp)</Label>
                <Input data-testid="np-price" type="number" placeholder="0" value={np.price} onChange={(e) => setNp({ ...np, price: Number(e.target.value) })} className="mt-1 bg-neutral-100 border-neutral-200 font-mono-num" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Stok</Label>
                <Input data-testid="np-stock" type="number" placeholder="0" value={np.stock} onChange={(e) => setNp({ ...np, stock: Number(e.target.value) })} className="mt-1 bg-neutral-100 border-neutral-200 font-mono-num" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Min. Stok</Label>
                <Input data-testid="np-threshold" type="number" placeholder="5" value={np.low_stock_threshold} onChange={(e) => setNp({ ...np, low_stock_threshold: Number(e.target.value) })} className="mt-1 bg-neutral-100 border-neutral-200 font-mono-num" />
              </div>
            </div>
            <Button data-testid="save-product-btn" onClick={saveProduct} className="w-full bg-crimson font-bold hover:bg-red-500">{editId ? "Simpan Perubahan" : "Simpan Produk"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!disc} onOpenChange={(o) => !o && setDisc(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Tag size={18} className="text-gold" /> Diskon · {disc?.name}</DialogTitle></DialogHeader>
          {disc && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Tipe Diskon</Label>
                <Select value={disc.discount_type || "none"} onValueChange={(v) => setDisc({ ...disc, discount_type: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="disc-type" className="mt-1 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Tanpa Diskon</SelectItem><SelectItem value="nominal">Nominal (Rp)</SelectItem><SelectItem value="percent">Persentase (%)</SelectItem></SelectContent>
                </Select>
              </div>
              {disc.discount_type && (
                <>
                  <div><Label className="text-xs text-muted-foreground">Nilai Diskon ({disc.discount_type === "percent" ? "%" : "Rp"})</Label>
                    <Input data-testid="disc-value" type="number" value={disc.discount_value} onChange={(e) => setDisc({ ...disc, discount_value: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200 font-mono-num" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs text-muted-foreground">Mulai (opsional)</Label><Input data-testid="disc-start" type="datetime-local" value={disc.discount_start} onChange={(e) => setDisc({ ...disc, discount_start: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200 text-xs" /></div>
                    <div><Label className="text-xs text-muted-foreground">Selesai (opsional)</Label><Input data-testid="disc-end" type="datetime-local" value={disc.discount_end} onChange={(e) => setDisc({ ...disc, discount_end: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200 text-xs" /></div>
                  </div>
                  <p className="text-xs text-muted-foreground">Kosongkan tanggal untuk diskon permanen. Harga coret akan tampil otomatis di Kasir.</p>
                </>
              )}
              <Button data-testid="save-disc-btn" onClick={saveDisc} className="w-full bg-crimson font-bold hover:bg-red-500">Simpan Diskon</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
